import {
  ArrowDownIcon,
  ArrowUpIcon,
} from '@heroicons/react/20/solid';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { DateRangePicker } from '@/components/common/DataTable/DateRangePicker';
import { PaginationControls } from '@/components/common/PaginationControls';
import { Pill, PillVariant } from '@/components/common/Pill';
import { TaskDetailDrawer } from '@/components/tasks-datatable/components/TaskDetailDrawer';
import { Button } from '@/components/ui/catalyst/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/catalyst/table';
import { Text } from '@/components/ui/catalyst/text';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { requiresDueDateChangeReason } from '@/constants/task-field-change-reasons';
import {
  FieldChangeReasonProvider,
  useFieldChangeReason,
} from '@/contexts/FieldChangeReasonContext';
import { useStartEnhancedBlandCallWorkflowMutation } from '@/services/api/blandService';
import { useCreateTaskUpdateMutation } from '@/services/api/tasksService';
import {
  UpdateSource,
  useAcceptAlternateContactMutation,
  useGetVoiceAiDashboardQuery,
  useToggleReviewedMutation,
  useToggleTaskUpdateReviewedMutation,
  useUpdateDueDateByTaskMutation,
  useUpdateDueDateMutation,
  VoiceAiDashboardCategory,
  VoiceAiDashboardRow,
} from '@/services/api/voiceAiDashboardService';
import { TaskUpdateVisibility } from '@/services/types/client-intake-types';
import { selectUser } from '@/state/selectors/root-selectors';
import { formatToISODate } from '@/utils/dateUtils';

/* ─── Constants ────────────────────────────────────────────────── */


const ACTIONABLE_NEXT_ACTIONS: Record<string, string> = {
  RESUBMIT_SAME_DESTINATION: 'Resubmit Request',
  RESUBMIT_NEW_DESTINATION: 'Resubmit Request',
  CALL_NEW_NUMBER: 'Call New Number',
  RESEARCH_NEW_NUMBER: 'Research New Number',
};

/** Upwork outcomes where the Options button is useful (resubmit / contact correction). */
const UPWORK_ACTIONABLE_OUTCOMES = new Set([
  'Needs resubmission or additional documents',
  'Wrong number or contact information',
]);

/**
 * Parse Upwork TaskUpdate markdown into clean summary lines.
 * Removes Outcome (shown separately), strips markdown formatting,
 * and reorders: Notes first, Provider Phone Used, then everything else.
 *
 * Continuation lines (non-bullet text following a `* **Key:** value` line)
 * are appended to the preceding key's value so multi-line notes are preserved.
 */
function formatUpworkSummary(raw: string): string[] {
  const parsed = raw
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .reduce<{ key: string; value: string }[]>((acc, line) => {
      // Match "* **Label:** value" or "**Label:** value"
      const m = line.match(/^\*?\s*\*\*(.+?):\*\*\s*(.+)/);
      if (m) {
        acc.push({ key: m[1].trim(), value: m[2].trim() });
      } else if (acc.length > 0 && !line.startsWith('@')) {
        // Continuation line — append to the most recent key's value
        acc[acc.length - 1].value += ` ${line}`;
      }
      return acc;
    }, []);

  // Remove Outcome (already in Next Action column)
  const filtered = parsed.filter((p) => p.key.toLowerCase() !== 'outcome');

  // Reorder: Notes first, Provider Phone Used second, rest after
  const notes = filtered.filter((p) => p.key.toLowerCase() === 'notes');
  const phone = filtered.filter(
    (p) => p.key.toLowerCase() === 'provider phone used',
  );
  const rest = filtered.filter(
    (p) =>
      p.key.toLowerCase() !== 'notes' &&
      p.key.toLowerCase() !== 'provider phone used',
  );

  return [...notes, ...phone, ...rest].map((p) => `${p.key}: ${p.value}`);
}

const CHANNEL_OPTIONS = [
  { value: 'fax', label: 'Fax' },
  { value: 'email', label: 'Email' },
  { value: 'mailing_address', label: 'Mail' },
];

const CHANNEL_LABELS: Record<string, string> = {
  fax: 'Fax Number',
  email: 'Email Address',
  mailing_address: 'Mailing Address',
};

const CHANNEL_PLACEHOLDERS: Record<string, string> = {
  fax: 'Enter fax number',
  email: 'Enter email address',
  mailing_address: 'Enter mailing address',
};

/* ─── Helpers ──────────────────────────────────────────────────── */

const formatDate = (iso: string): string => {
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const toInputDate = (iso: string): string => iso.slice(0, 10);

const getActionLabel = (nextActionRaw: string): string | null =>
  ACTIONABLE_NEXT_ACTIONS[nextActionRaw] ?? null;

const getChannelLabel = (channel: string): string =>
  CHANNEL_LABELS[channel] ?? 'Value';

const getChannelPlaceholder = (channel: string): string =>
  CHANNEL_PLACEHOLDERS[channel] ?? 'Enter value';

const isResubmitAction = (action: string): boolean =>
  action === 'RESUBMIT_SAME_DESTINATION' ||
  action === 'RESUBMIT_NEW_DESTINATION';

const isCallAction = (action: string): boolean =>
  action === 'CALL_NEW_NUMBER' || action === 'RESEARCH_NEW_NUMBER';

const getResubmitDefaults = (
  row: VoiceAiDashboardRow,
): { channel: string; value: string } => {
  if (row.next_action_raw === 'RESUBMIT_SAME_DESTINATION') {
    return {
      channel: row.current_channel_type || 'fax',
      value: row.current_channel_value,
    };
  }
  return {
    channel: row.alternate_handle_type || 'fax',
    value: row.alternate_value,
  };
};

type SortField = 'created_at' | 'category' | 'matter_name';

/* ─── Resizable column IDs ────────────────────────────────────── */

const RESIZABLE_COLUMNS = [
  'matter',
  'request_type',
  'provider',
  'created',
  'next_action',
  'summary',
] as const;

type ResizableColumnId = (typeof RESIZABLE_COLUMNS)[number];

const DEFAULT_COLUMN_WIDTHS: Record<ResizableColumnId, string> = {
  matter: '15%',
  request_type: '8%',
  provider: '10%',
  created: '9%',
  next_action: '10%',
  summary: '22%',
};

/* ─── Small Sub-Components ─────────────────────────────────────── */

const SOURCE_PILL_VARIANTS: Record<UpdateSource, PillVariant> = {
  'Voice AI': 'gray',
  BPO: 'gray',
  Upwork: 'gray',
};

const SourcePill: React.FC<{ source: UpdateSource }> = ({ source }) => (
  <Pill variant={SOURCE_PILL_VARIANTS[source]} size="sm">
    {source}
  </Pill>
);

const SOURCES: UpdateSource[] = ['Voice AI', 'BPO', 'Upwork'];

const ReviewedToggle: React.FC<{
  runId?: string;
  taskUpdateId?: string;
  reviewed: boolean;
}> = ({ runId, taskUpdateId, reviewed }) => {
  const [toggleReviewed] = useToggleReviewedMutation();
  const [toggleTaskUpdateReviewed] = useToggleTaskUpdateReviewedMutation();

  const handleToggle = useCallback(async () => {
    try {
      if (taskUpdateId) {
        await toggleTaskUpdateReviewed({ taskUpdateId, reviewed: !reviewed }).unwrap();
      } else if (runId) {
        await toggleReviewed({ runId, reviewed: !reviewed }).unwrap();
      }
    } catch {
      toast.error('Failed to update reviewed status');
    }
  }, [toggleReviewed, toggleTaskUpdateReviewed, runId, taskUpdateId, reviewed]);

  if (reviewed) {
    return (
      <button
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 dark:text-green-400 dark:bg-green-950/30 dark:hover:bg-green-950/50 transition-colors"
        aria-label="Mark as not reviewed"
      >
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        Reviewed
      </button>
    );
  }

  return (
    <Button
      outline
      onClick={handleToggle}
      className="!px-2 !py-0.5 !text-xs"
      aria-label="Mark as reviewed"
    >
      Review
    </Button>
  );
};

const DueDatePicker: React.FC<{
  runId: string;
  taskId: string;
  source: UpdateSource;
  dueDate: string | null;
  taskName: string;
  reviewed: boolean;
}> = ({ runId, taskId, source, dueDate, taskName, reviewed }) => {
  const [updateDueDate] = useUpdateDueDateMutation();
  const [updateDueDateByTask] = useUpdateDueDateByTaskMutation();
  const [toggleReviewed] = useToggleReviewedMutation();
  const { requestChangeReason } = useFieldChangeReason();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      if (!val) return;

      const newDateStr = `${val}T00:00:00-05:00`;
      const oldDateStr = dueDate ?? null;

      let changeReason: string | undefined;

      if (requiresDueDateChangeReason(oldDateStr, newDateStr)) {
        const result = await requestChangeReason({
          fieldType: 'due_date',
          oldValue: oldDateStr,
          newValue: newDateStr,
          taskName,
        });

        if (!result) {
          if (inputRef.current) {
            inputRef.current.value = dueDate ? toInputDate(dueDate) : '';
          }
          return;
        }

        changeReason = result.reasonDetail || result.reason;
      }

      if (!changeReason) {
        const result = await requestChangeReason({
          fieldType: 'due_date',
          oldValue: oldDateStr,
          newValue: newDateStr,
          taskName,
        });

        if (!result) {
          if (inputRef.current) {
            inputRef.current.value = dueDate ? toInputDate(dueDate) : '';
          }
          return;
        }

        changeReason = result.reasonDetail || result.reason;
      }

      try {
        if (source === 'Upwork') {
          await updateDueDateByTask({
            taskId,
            due_date: newDateStr,
            change_reason: changeReason,
          }).unwrap();
        } else {
          await updateDueDate({
            runId,
            due_date: newDateStr,
            change_reason: changeReason,
          }).unwrap();
        }
        toast.success('Due date updated');

        // Auto-mark as reviewed when due date is changed (Voice AI only)
        if (!reviewed && source === 'Voice AI') {
          try {
            await toggleReviewed({ runId, reviewed: true }).unwrap();
          } catch {
            // Silently fail — the date update was the primary action
          }
        }
      } catch {
        toast.error('Failed to update due date');
      }
    },
    [updateDueDate, updateDueDateByTask, toggleReviewed, runId, taskId, source, dueDate, taskName, reviewed, requestChangeReason],
  );

  return (
    <input
      ref={inputRef}
      type="date"
      value={dueDate ? toInputDate(dueDate) : ''}
      onChange={handleChange}
      className="w-full border border-gray-500 px-0.5 py-0.5 text-xs font-medium text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-zinc-500 dark:bg-zinc-800 dark:text-zinc-200"
      aria-label="Next action date"
    />
  );
};

/* ─── Day-push options ─────────────────────────────────────────── */

const DAY_OPTIONS = [1, 3, 5, 7, 14] as const;

const computeNewDate = (
  baseDate: string | null,
  days: number,
): { iso: string; display: string } => {
  const base = baseDate ? new Date(baseDate) : new Date();
  const result = new Date(base);
  result.setDate(result.getDate() + days);
  const iso = formatToISODate(result);
  const display = result.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return { iso, display };
};

/* ─── Action types for modal ──────────────────────────────────── */

type ActionType =
  | 'RESUBMIT_SAME_DESTINATION'
  | 'RESUBMIT_NEW_DESTINATION'
  | 'CALL_NEW_NUMBER'
  | 'RESEARCH_NEW_NUMBER';

const ALL_ACTIONS: { value: ActionType; label: string }[] = [
  { value: 'RESUBMIT_SAME_DESTINATION', label: 'Resubmit Request (Same Destination)' },
  { value: 'RESUBMIT_NEW_DESTINATION', label: 'Resubmit Request (New Destination)' },
  { value: 'CALL_NEW_NUMBER', label: 'Call New Number' },
  { value: 'RESEARCH_NEW_NUMBER', label: 'Research New Number' },
];

/* ─── Action Modal ────────────────────────────────────────────── */

const UPWORK_ACTIONS: { value: ActionType; label: string }[] = [
  { value: 'RESUBMIT_SAME_DESTINATION', label: 'Resubmit Request (Same Destination)' },
  { value: 'RESUBMIT_NEW_DESTINATION', label: 'Resubmit Request (New Destination)' },
  { value: 'CALL_NEW_NUMBER', label: 'Call New Number' },
  { value: 'RESEARCH_NEW_NUMBER', label: 'Research New Number' },
];

const ActionModal: React.FC<{
  row: VoiceAiDashboardRow;
  onClose: () => void;
}> = ({ row, onClose }) => {
  const isUpwork = row.source === 'Upwork';
  const getUpworkDefaultAction = (): ActionType => {
    if (row.outcome === 'Wrong number or contact information') return 'RESEARCH_NEW_NUMBER';
    return 'RESUBMIT_SAME_DESTINATION';
  };
  const defaultAction: ActionType = isUpwork
    ? getUpworkDefaultAction()
    : (row.next_action_raw as ActionType);
  const [selectedAction, setSelectedAction] = useState<ActionType>(
    defaultAction,
  );
  const resubmitDefaults = getResubmitDefaults(row);
  const [channel, setChannel] = useState(resubmitDefaults.channel);
  const [channelValue, setChannelValue] = useState(resubmitDefaults.value);
  const [phone, setPhone] = useState(
    // For Voice AI RESEARCH_NEW_NUMBER the phone is blank (needs research).
    // For Upwork rows the corrected phone is already known.
    row.next_action_raw === 'RESEARCH_NEW_NUMBER' && !isUpwork
      ? ''
      : row.new_phone,
  );

  // Reset form fields when switching actions
  const handleActionChange = useCallback(
    (action: ActionType) => {
      setSelectedAction(action);
      userHasEdited.current = false;
      if (isResubmitAction(action)) {
        const defaults = getResubmitDefaults(row);
        setChannel(defaults.channel);
        setChannelValue(defaults.value);
      } else if (action === 'CALL_NEW_NUMBER') {
        setPhone(row.new_phone || '');
      } else if (action === 'RESEARCH_NEW_NUMBER') {
        // For Upwork we already have the corrected phone
        setPhone(isUpwork ? (row.new_phone || '') : '');
      }
    },
    [row, isUpwork],
  );
  const [pushDays, setPushDays] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [acceptAlternate] = useAcceptAlternateContactMutation();
  const [startWorkflow] = useStartEnhancedBlandCallWorkflowMutation();
  const [updateDueDate] = useUpdateDueDateMutation();
  const [updateDueDateByTask] = useUpdateDueDateByTaskMutation();
  const [createTaskUpdate] = useCreateTaskUpdateMutation();
  const [toggleReviewed] = useToggleReviewedMutation();

  const isResubmit = isResubmitAction(selectedAction);
  const isCall = isCallAction(selectedAction);

  // Build auto-drafted message
  const draftMessage = useMemo(() => {
    const parts: string[] = [];
    if (isResubmit) {
      const channelLabel =
        CHANNEL_OPTIONS.find((o) => o.value === channel)?.label ?? channel;
      parts.push(
        `Resubmitted the request via ${channelLabel.toLowerCase()} on ${channelValue || '(pending)'}`,
      );
    } else if (selectedAction === 'CALL_NEW_NUMBER') {
      parts.push(`Called new number: ${phone || '(pending)'}`);
    } else if (selectedAction === 'RESEARCH_NEW_NUMBER') {
      parts.push(`Researching new number: ${phone || '(pending)'}`);
    }
    if (pushDays !== null) {
      parts.push(`Pushing next action by ${pushDays} day${pushDays === 1 ? '' : 's'}`);
    }
    return parts.join('. ') + (parts.length > 0 ? '.' : '');
  }, [isResubmit, selectedAction, channel, channelValue, phone, pushDays]);

  const [editedMessage, setEditedMessage] = useState('');

  // Sync auto-draft into editable message (only when auto-draft changes and user hasn't manually edited)
  const userHasEdited = useRef(false);
  useEffect(() => {
    if (!userHasEdited.current) {
      setEditedMessage(draftMessage);
    }
  }, [draftMessage]);

  const handleMessageChange = (val: string) => {
    userHasEdited.current = true;
    setEditedMessage(val);
  };

  const newDateInfo = pushDays !== null ? computeNewDate(row.due_date, pushDays) : null;

  const validateValue = useCallback((): string | null => {
    if (isResubmit) {
      const trimmed = channelValue.trim();
      if (!trimmed) return 'Please enter a value';
      if (channel === 'fax') {
        const digits = trimmed.replace(/\D/g, '');
        if (digits.length !== 10) return 'Fax number must be exactly 10 digits (US format)';
      }
      if (channel === 'email' && !trimmed.includes('@')) return 'Please enter a valid email address';
    }
    if (isCall) {
      const trimmed = phone.trim();
      if (!trimmed) return 'Please enter a phone number';
      const digits = trimmed.replace(/\D/g, '');
      if (digits.length !== 10) return 'Phone number must be exactly 10 digits (US format)';
    }
    return null;
  }, [isResubmit, isCall, channelValue, channel, phone]);

  const canSubmit = useMemo(() => validateValue() === null, [validateValue]);

  const handleSubmit = useCallback(async () => {
    const validationError = validateValue();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setIsSubmitting(true);
    const errors: string[] = [];
    let actionSucceeded = true;

    // 1. Execute the primary action
    try {
      if (isResubmit) {
        await acceptAlternate({
          matterId: row.matter_id,
          providerId: row.provider_id,
          requestId: row.treatment_record_request_id,
          handle_type: channel,
          value: channelValue.trim(),
        }).unwrap();
      } else if (isCall) {
        await startWorkflow({
          matterId: row.matter_id,
          taskId: row.task_id,
          preferredPhoneNumber: phone.trim(),
        }).unwrap();
      }
    } catch {
      errors.push('action');
      actionSucceeded = false;
    }

    // Only proceed with remaining steps if the primary action succeeded
    if (actionSucceeded) {
      // 2. Push due date if days selected
      try {
        if (pushDays !== null && newDateInfo) {
          const newDateStr = `${newDateInfo.iso}T00:00:00-05:00`;
          if (isUpwork) {
            await updateDueDateByTask({
              taskId: row.task_id,
              due_date: newDateStr,
              change_reason: editedMessage || draftMessage,
            }).unwrap();
          } else {
            await updateDueDate({
              runId: row.voice_ai_agent_run_id,
              due_date: newDateStr,
              change_reason: editedMessage || draftMessage,
            }).unwrap();
          }
        }
      } catch {
        errors.push('due date');
      }

      // 3. Post task update (skip if date was pushed — backend already posts via change_reason)
      try {
        if (editedMessage.trim() && row.task_id && row.matter_id && pushDays === null) {
          await createTaskUpdate({
            matterId: row.matter_id,
            taskId: row.task_id,
            update: {
              update: editedMessage.trim(),
              visibility: TaskUpdateVisibility.PRIVATE,
            },
          }).unwrap();
        }
      } catch {
        errors.push('task update');
      }

      // 4. Mark as reviewed (Voice AI only)
      try {
        if (!row.reviewed && !isUpwork) {
          await toggleReviewed({
            runId: row.voice_ai_agent_run_id,
            reviewed: true,
          }).unwrap();
        }
      } catch {
        errors.push('review');
      }
    }

    setIsSubmitting(false);

    if (errors.length === 0) {
      toast.success('Action completed successfully');
      onClose();
    } else {
      toast.error(`Failed: ${errors.join(', ')}. ${actionSucceeded ? 'Other steps completed.' : 'No further actions taken.'}`);
      onClose();
    }
  }, [
    validateValue,
    isResubmit,
    isCall,
    isUpwork,
    acceptAlternate,
    startWorkflow,
    updateDueDate,
    updateDueDateByTask,
    createTaskUpdate,
    toggleReviewed,
    row,
    channel,
    channelValue,
    phone,
    pushDays,
    newDateInfo,
    editedMessage,
    draftMessage,
    onClose,
  ]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg dark:bg-zinc-900 dark:border-zinc-700">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold dark:text-white">
            {getActionLabel(row.next_action_raw) ?? 'Take Action'}
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-600 dark:text-zinc-400">
            {row.matter_name} &middot; {row.provider_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Action selector */}
          <fieldset>
            <legend className="mb-2 block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Action
            </legend>
            <div className="space-y-1.5">
              {/* eslint-disable jsx-a11y/label-has-associated-control */}
              {(isUpwork ? UPWORK_ACTIONS : ALL_ACTIONS).map((action) => (
                <label
                  key={action.value}
                  className={`flex cursor-pointer items-center gap-2.5 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                    selectedAction === action.value
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-950/30 dark:text-indigo-300'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:border-zinc-600 dark:hover:bg-zinc-800'
                  }`}
                >
                  <input
                    type="radio"
                    name="action"
                    value={action.value}
                    checked={selectedAction === action.value}
                    onChange={() => handleActionChange(action.value)}
                    className="accent-indigo-600"
                  />
                  {action.label}
                  {action.value === row.next_action_raw && (
                    <span className="ml-auto text-xs font-medium text-indigo-500 dark:text-indigo-400">
                      Recommended
                    </span>
                  )}
                </label>
              ))}
              {/* eslint-enable jsx-a11y/label-has-associated-control */}
            </div>
          </fieldset>

          {/* Action-specific fields */}
          {isResubmit && (
            <div className="space-y-3">
              <div>
                <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-zinc-300">
                  Channel
                </span>
                <select
                  aria-label="Channel"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                >
                  {CHANNEL_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-zinc-300">
                  {getChannelLabel(channel)}
                </span>
                <input
                  aria-label={getChannelLabel(channel)}
                  type="text"
                  value={channelValue}
                  onChange={(e) => setChannelValue(e.target.value)}
                  placeholder={getChannelPlaceholder(channel)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                />
              </div>
            </div>
          )}

          {isCall && (
            <div>
              <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-zinc-300">
                Phone Number
              </span>
              <input
                aria-label="Phone Number"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter phone number"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
              />
            </div>
          )}

          {/* Push next action date */}
          <div>
            <span className="mb-2 block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Push next action date by
            </span>
            <div className="flex flex-wrap gap-2">
              {DAY_OPTIONS.map((days) => (
                <button
                  key={days}
                  type="button"
                  onClick={() =>
                    setPushDays((prev) => (prev === days ? null : days))
                  }
                  className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                    pushDays === days
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700'
                  }`}
                >
                  +{days} day{days === 1 ? '' : 's'}
                </button>
              ))}
            </div>
            {newDateInfo && (
              <p className="mt-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400">
                New date: {newDateInfo.display}
              </p>
            )}
          </div>

          {/* Auto-drafted task update */}
          <div>
            <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-zinc-300">
              Task Update
            </span>
            <textarea
              aria-label="Task Update"
              value={editedMessage}
              onChange={(e) => handleMessageChange(e.target.value)}
              rows={2}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
            />
          </div>
        </div>

        <DialogFooter className="mt-2">
          <Button outline onClick={onClose} className="!text-sm">
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={handleSubmit}
            disabled={isSubmitting || !canSubmit}
            className="!text-sm"
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ─── Filter Bar ───────────────────────────────────────────────── */

const DashboardFilterBar: React.FC<{
  sinceDate: string;
  untilDate: string;
  onDateRangeChange: (startDate: string, endDate: string) => void;
  assigneeId: string;
  onAssigneeChange: (id: string) => void;
  sourceFilter: string;
  onSourceChange: (s: string) => void;
  showReviewed: boolean;
  onShowReviewedChange: (show: boolean) => void;
  assignees: { id: string; name: string }[];
  totalCount: number;
  startIndex: number;
  endIndex: number;
  isLoading: boolean;
  onRefresh: () => void;
}> = ({
  sinceDate,
  untilDate,
  onDateRangeChange,
  assigneeId,
  onAssigneeChange,
  sourceFilter,
  onSourceChange,
  showReviewed,
  onShowReviewedChange,
  assignees,
  totalCount,
  startIndex,
  endIndex,
  isLoading,
  onRefresh,
}) => (
  <div className="flex flex-shrink-0 flex-wrap items-center gap-3 border-b border-zinc-300 px-4 py-2 dark:border-zinc-700">
    {/* Date range filter */}
    <DateRangePicker
      startDate={sinceDate}
      endDate={untilDate}
      onRangeChange={onDateRangeChange}
      placeholder="Select date range"
    />

    {/* Filters */}
    <div className="flex items-center gap-2">
      <label htmlFor="dashboard-assignee" className="flex items-center gap-1.5">
        <span className="text-xs font-semibold text-black dark:text-white">
          Assignee
        </span>
        <select
          id="dashboard-assignee"
          value={assigneeId}
          onChange={(e) => onAssigneeChange(e.target.value)}
          className="h-7 rounded-md border border-zinc-950/10 bg-transparent px-2 pr-7 text-xs font-medium text-zinc-950 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-white/10 dark:bg-white/5 dark:text-white"
        >
          <option value="">All</option>
          {assignees.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </label>
    </div>


    <div className="flex items-center gap-2">
      <label htmlFor="dashboard-source" className="flex items-center gap-1.5">
        <span className="text-xs font-semibold text-black dark:text-white">
          Source
        </span>
        <select
          id="dashboard-source"
          value={sourceFilter}
          onChange={(e) => onSourceChange(e.target.value)}
          className="h-7 rounded-md border border-zinc-950/10 bg-transparent px-2 pr-7 text-xs font-medium text-zinc-950 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-white/10 dark:bg-white/5 dark:text-white"
        >
          <option value="">All</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
    </div>

    <Button
      plain
      onClick={() => onShowReviewedChange(!showReviewed)}
      className={`!h-7 !px-2 !text-xs ${showReviewed ? 'before:absolute before:-inset-px before:rounded-md before:bg-zinc-950/5 dark:before:bg-white/10' : ''}`}
    >
      {showReviewed ? 'Showing all' : 'Hiding reviewed'}
    </Button>

    {/* Legend + count + refresh (right side) */}
    <div className="ml-auto flex items-center gap-3">
      <div className="flex items-center gap-1.5">
        <Pill variant="red" size="sm">Legal Ops</Pill>
        <Pill variant="orange" size="sm">BPO</Pill>
        <Pill variant="green" size="sm">Provider</Pill>
      </div>

      {totalCount > 0 && (
        <Text className="!text-xs text-zinc-600 dark:text-zinc-400 whitespace-nowrap">
          Showing {startIndex}-{endIndex} of {totalCount} results
        </Text>
      )}

      <Button
        color="blue"
        onClick={onRefresh}
        disabled={isLoading}
        className="!h-7 !px-2.5 !text-xs gap-1"
      >
        <ArrowPathIcon
          className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`}
        />
        Refresh
      </Button>
    </div>
  </div>
);

/* ─── Sortable Header with Resize Handle ──────────────────────── */

const SortableColumnHeader: React.FC<{
  label: string;
  field: SortField;
  currentOrdering: string;
  onSort: (f: SortField) => void;
  columnId?: ResizableColumnId;
  columnWidth?: string;
  onResizeStart?: (e: React.MouseEvent, columnId: string) => void;
}> = ({ label, field, currentOrdering, onSort, columnId, columnWidth, onResizeStart }) => {
  const currentField = currentOrdering.replace('-', '');
  const isActive = currentField === field;
  const isDesc = currentOrdering.startsWith('-');

  return (
    <TableHeader
      className="relative cursor-pointer select-none font-semibold leading-4 !py-1 text-black dark:text-white"
      onClick={() => onSort(field)}
      style={columnWidth ? { width: columnWidth } : undefined}
    >
      <div className="inline-flex items-center gap-1">
        <span>{label}</span>
        {isActive && (
          <span className="text-blue-600 dark:text-blue-400">
            {isDesc ? (
              <ArrowDownIcon className="h-4 w-4" />
            ) : (
              <ArrowUpIcon className="h-4 w-4" />
            )}
          </span>
        )}
      </div>
      {columnId && onResizeStart && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className="absolute right-0 top-0 bottom-0 w-3 cursor-col-resize group flex items-center justify-center"
          onMouseDown={(e) => {
            e.stopPropagation();
            onResizeStart(e, columnId);
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="w-[3px] h-4 bg-zinc-300 dark:bg-zinc-600 group-hover:bg-blue-500 transition-colors" />
        </div>
      )}
    </TableHeader>
  );
};

const ResizableHeader: React.FC<{
  label: string;
  columnId: ResizableColumnId;
  columnWidth?: string;
  onResizeStart: (e: React.MouseEvent, columnId: string) => void;
}> = ({ label, columnId, columnWidth, onResizeStart }) => (
  <TableHeader
    className="relative font-semibold leading-4 !py-1 text-black dark:text-white"
    style={columnWidth ? { width: columnWidth } : undefined}
  >
    {label}
    {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
    <div
      className="absolute right-0 top-0 bottom-0 w-3 cursor-col-resize group flex items-center justify-center"
      onMouseDown={(e) => {
        e.stopPropagation();
        onResizeStart(e, columnId);
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-[3px] h-4 bg-zinc-300 dark:bg-zinc-600 group-hover:bg-blue-500 transition-colors" />
    </div>
  </TableHeader>
);

/* ─── Dashboard Row ────────────────────────────────────────────── */

const DashboardRow: React.FC<{
  row: VoiceAiDashboardRow;
  onTaskClick?: (taskId: string, matterId: string) => void;
  onOpenModal: (row: VoiceAiDashboardRow) => void;
}> = ({ row, onTaskClick, onOpenModal }) => {
  const rowClass = row.reviewed ? 'opacity-50' : '';
  const isUpwork = row.source === 'Upwork';
  const hasAction = isUpwork
    ? UPWORK_ACTIONABLE_OUTCOMES.has(row.outcome)
    : getActionLabel(row.next_action_raw) !== null;

  return (
    <TableRow
      className={`hover:bg-gray-50 dark:hover:bg-zinc-800 h-6 ${rowClass}`}
    >
      <TableCell className="max-w-0 !py-1 font-medium leading-4">
        <a
          href={`/matters/${row.matter_id}`}
          target="_blank"
          rel="noopener noreferrer"
          className="block truncate text-blue-700 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
        >
          {row.matter_name}
        </a>
      </TableCell>
      <TableCell className="!py-1 font-medium leading-4">
        {row.task_id && row.matter_id ? (
          <button
            type="button"
            onClick={() => onTaskClick?.(row.task_id, row.matter_id)}
            className="whitespace-normal break-words text-left text-blue-700 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
          >
            {row.request_type}
          </button>
        ) : (
          <div className="whitespace-normal break-words">{row.request_type}</div>
        )}
      </TableCell>
      <TableCell className="!py-1 font-medium leading-4">
        <div className="whitespace-normal break-words">
          {row.provider_name}
        </div>
      </TableCell>
      <TableCell className="!py-1 font-medium leading-4 whitespace-nowrap text-zinc-600 dark:text-zinc-400">
        {formatDate(row.created_at)}
      </TableCell>
      <TableCell className="!py-1 whitespace-nowrap">
        <SourcePill source={row.source} />
      </TableCell>
      <TableCell className="!py-1 !pb-2 font-medium leading-4">
        <div className="whitespace-normal break-words">{row.next_action_display}</div>
        {hasAction && (
          <button
            type="button"
            onClick={() => onOpenModal(row)}
            className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 shadow-sm hover:bg-indigo-100 hover:border-indigo-300 active:bg-indigo-150 transition-colors dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300 dark:hover:bg-indigo-950/60 dark:hover:border-indigo-600"
          >
            Options
          </button>
        )}
      </TableCell>
      <TableCell className="!py-1 font-medium leading-4 text-zinc-600 dark:text-zinc-400">
        <div className="whitespace-normal break-words leading-snug">
          {isUpwork ? (
            formatUpworkSummary(row.update_text).map((line, i) => (
              <div key={i}>{line}</div>
            ))
          ) : (
            row.summary
          )}
        </div>
      </TableCell>
      <TableCell className="!py-1" style={{ width: '110px' }}>
        <DueDatePicker
          runId={row.voice_ai_agent_run_id}
          taskId={row.task_id}
          source={row.source}
          dueDate={row.due_date}
          taskName={row.task_name}
          reviewed={row.reviewed}
        />
      </TableCell>
      <TableCell className="!py-1" style={{ width: '70px' }}>
        <ReviewedToggle
          runId={isUpwork ? undefined : row.voice_ai_agent_run_id}
          taskUpdateId={isUpwork ? row.task_update_id : undefined}
          reviewed={row.reviewed}
        />
      </TableCell>
    </TableRow>
  );
};

/* ─── Main Dashboard ───────────────────────────────────────────── */

const COLUMN_COUNT = 9;

const VoiceAiDashboardInner: React.FC = () => {
  const currentUser = useSelector(selectUser);

  // Date range state (default: last 7 days)
  const [sinceDate, setSinceDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return formatToISODate(d);
  });
  const [untilDate, setUntilDate] = useState(() => formatToISODate(new Date()));

  // Pagination & filter state
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [ordering, setOrdering] = useState('-created_at');
  const [assigneeId, setAssigneeId] = useState(currentUser?.id ?? '');
  const [categoryFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [showReviewed, setShowReviewed] = useState(false);

  // UI state
  const [selectedTask, setSelectedTask] = useState<{
    taskId: string;
    matterId: string;
  } | null>(null);
  const [modalRow, setModalRow] = useState<VoiceAiDashboardRow | null>(null);

  // Column resizing state
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});
  const resizeStateRef = useRef<{
    columnId: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!resizeStateRef.current) return;
    const { columnId, startX, startWidth } = resizeStateRef.current;
    const deltaX = e.clientX - startX;
    const newWidth = Math.max(80, startWidth + deltaX);
    setColumnWidths((prev) => ({
      ...prev,
      [columnId]: `${newWidth}px`,
    }));
  }, []);

  const handleResizeEndRef = useRef<() => void>(() => {});

  const handleResizeEnd = useCallback(() => {
    resizeStateRef.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEndRef.current);
  }, [handleResizeMove]);

  useEffect(() => {
    handleResizeEndRef.current = handleResizeEnd;
  }, [handleResizeEnd]);

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEndRef.current);
    };
  }, [handleResizeMove]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent, columnId: string) => {
      e.preventDefault();
      e.stopPropagation();
      const headerEl = (e.currentTarget as HTMLElement).closest('th');
      const startWidth = headerEl
        ? headerEl.getBoundingClientRect().width
        : 200;
      resizeStateRef.current = {
        columnId,
        startX: e.clientX,
        startWidth,
      };
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEndRef.current);
    },
    [handleResizeMove],
  );

  const handleTaskClick = useCallback(
    (taskId: string, matterId: string) => {
      setSelectedTask({ taskId, matterId });
    },
    [],
  );

  const handleCloseTaskDetail = useCallback(() => {
    setSelectedTask(null);
  }, []);

  // Build query filters
  const filters = useMemo(
    () => ({
      since_date: sinceDate || undefined,
      until_date: untilDate || undefined,
      assignee_id: assigneeId || undefined,
      category: categoryFilter
        ? (categoryFilter as VoiceAiDashboardCategory)
        : undefined,
      source: sourceFilter
        ? (sourceFilter as UpdateSource)
        : undefined,
      reviewed: showReviewed ? 'all' : 'false',
      limit,
      offset,
      ordering,
    }),
    [sinceDate, untilDate, assigneeId, categoryFilter, sourceFilter, showReviewed, limit, offset, ordering],
  );

  const { data, isLoading, error, refetch } =
    useGetVoiceAiDashboardQuery(filters);

  // Derived pagination values
  const totalCount = data?.count ?? 0;
  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));
  const startIndex = totalCount > 0 ? offset + 1 : 0;
  const endIndex = Math.min(offset + limit, totalCount);

  const rows = data?.rows ?? [];

  // Sort handling (server-side)
  const handleSort = useCallback(
    (field: SortField) => {
      const currentField = ordering.replace('-', '');
      const isDesc = ordering.startsWith('-');

      if (currentField === field) {
        setOrdering(isDesc ? field : `-${field}`);
      } else {
        setOrdering(field === 'created_at' ? `-${field}` : field);
      }
      setOffset(0);
    },
    [ordering],
  );

  // Page change handlers
  const handlePageChange = useCallback(
    (page: number) => {
      setOffset((page - 1) * limit);
    },
    [limit],
  );

  const handlePageSizeChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setOffset(0);
  }, []);

  // Filter change handlers (reset to page 1)
  const handleAssigneeChange = useCallback((id: string) => {
    setAssigneeId(id);
    setOffset(0);
  }, []);

  const handleSourceChange = useCallback((s: string) => {
    setSourceFilter(s);
    setOffset(0);
  }, []);

  const handleShowReviewedChange = useCallback((show: boolean) => {
    setShowReviewed(show);
    setOffset(0);
  }, []);

  const handleDateRangeChange = useCallback((start: string, end: string) => {
    setSinceDate(start);
    setUntilDate(end);
    setOffset(0);
  }, []);

  if (error) {
    return (
      <div className="flex h-[calc(100vh-64px)] flex-col items-center justify-center bg-gray-50 dark:bg-zinc-950">
        <h2 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
          Failed to load dashboard
        </h2>
        <p className="mb-4 text-sm font-medium text-gray-600 dark:text-zinc-400">
          Unable to fetch Voice AI dashboard data.
        </p>
        <Button color="blue" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-64px)] flex-col gap-2 overflow-hidden bg-gray-50 px-10 py-2 dark:bg-zinc-950">
      {/* Title */}
      <div className="flex flex-shrink-0 items-center justify-between">
        <h1 className="font-serif text-4xl font-medium text-black dark:text-white">
          Voice AI Dashboard
        </h1>
      </div>

      {/* Table card */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-900">
        {/* Filter bar */}
        <DashboardFilterBar
          sinceDate={sinceDate}
          untilDate={untilDate}
          onDateRangeChange={handleDateRangeChange}
          assigneeId={assigneeId}
          onAssigneeChange={handleAssigneeChange}
          sourceFilter={sourceFilter}
          onSourceChange={handleSourceChange}
          showReviewed={showReviewed}
          onShowReviewedChange={handleShowReviewedChange}
          assignees={data?.assignees ?? []}
          totalCount={totalCount}
          startIndex={startIndex}
          endIndex={endIndex}
          isLoading={isLoading}
          onRefresh={refetch}
        />

        {/* Table content */}
        <div className="flex-1 overflow-auto [&_table]:table-fixed [&_table]:w-full [&>.flow-root]:h-full [&>.flow-root>div]:!overflow-visible [&>.flow-root>div]:h-full [&>.flow-root>div>div]:h-full">
          <Table
            dense
            className="[--gutter:--spacing(6)]"
          >
            <TableHead className="sticky top-0 z-10 bg-white dark:bg-zinc-900 [&_tr]:border-b [&_tr]:border-zinc-300 dark:[&_tr]:border-zinc-700 shadow-[0_1px_0_0_rgb(212,212,216)] dark:shadow-[0_1px_0_0_rgb(63,63,70)]">
              <TableRow>
                <SortableColumnHeader
                  label="Matter"
                  field="matter_name"
                  currentOrdering={ordering}
                  onSort={handleSort}
                  columnId="matter"
                  columnWidth={columnWidths.matter || DEFAULT_COLUMN_WIDTHS.matter}
                  onResizeStart={handleResizeStart}
                />
                <ResizableHeader
                  label="Request Type"
                  columnId="request_type"
                  columnWidth={columnWidths.request_type || DEFAULT_COLUMN_WIDTHS.request_type}
                  onResizeStart={handleResizeStart}
                />
                <ResizableHeader
                  label="Provider"
                  columnId="provider"
                  columnWidth={columnWidths.provider || DEFAULT_COLUMN_WIDTHS.provider}
                  onResizeStart={handleResizeStart}
                />
                <SortableColumnHeader
                  label="Created"
                  field="created_at"
                  currentOrdering={ordering}
                  onSort={handleSort}
                  columnId="created"
                  columnWidth={columnWidths.created || DEFAULT_COLUMN_WIDTHS.created}
                  onResizeStart={handleResizeStart}
                />
                <TableHeader
                  className="font-semibold leading-4 !py-1 text-black dark:text-white"
                  style={{ width: '70px' }}
                >
                  Source
                </TableHeader>
                <ResizableHeader
                  label="Next Action"
                  columnId="next_action"
                  columnWidth={columnWidths.next_action || DEFAULT_COLUMN_WIDTHS.next_action}
                  onResizeStart={handleResizeStart}
                />
                <ResizableHeader
                  label="Summary"
                  columnId="summary"
                  columnWidth={columnWidths.summary || DEFAULT_COLUMN_WIDTHS.summary}
                  onResizeStart={handleResizeStart}
                />
                <TableHeader
                  className="font-semibold leading-4 !py-1 text-black dark:text-white"
                  style={{ width: '110px' }}
                >
                  Due Date
                </TableHeader>
                <TableHeader
                  className="font-semibold leading-4 !py-1 text-black dark:text-white"
                  style={{ width: '70px' }}
                >
                  Reviewed
                </TableHeader>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className="border-none p-0 text-center"
                  >
                    <div className="flex items-center justify-center py-16">
                      <div className="mb-3 inline-block h-6 w-6 animate-spin rounded-full border-2 border-gray-500 border-t-blue-600" />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className="border-none p-0 text-center"
                  >
                    <div className="flex items-center justify-center py-16">
                      <Text className="text-zinc-500 dark:text-zinc-400">
                        No results found matching your filters.
                      </Text>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                rows.map((row) => (
                  <DashboardRow
                    key={row.row_id || row.voice_ai_agent_run_id}
                    row={row}
                    onTaskClick={handleTaskClick}
                    onOpenModal={setModalRow}
                  />
                ))}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {!isLoading && rows.length > 0 && (
          <div className="flex-shrink-0">
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={limit}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>

      {/* Task detail drawer */}
      {selectedTask && (
        <TaskDetailDrawer
          taskId={selectedTask.taskId}
          matterId={selectedTask.matterId}
          onClose={handleCloseTaskDetail}
        />
      )}

      {/* Action modal */}
      {modalRow && (
        <ActionModal
          row={modalRow}
          onClose={() => setModalRow(null)}
        />
      )}
    </div>
  );
};

export const VoiceAiDashboard: React.FC = () => {
  return (
    <FieldChangeReasonProvider>
      <VoiceAiDashboardInner />
    </FieldChangeReasonProvider>
  );
};
